import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Download, Copy, Share2, Film, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Public share page for a promo video — anyone can view / download / grab caption.
 *  URL pattern: /promo/:id — matches what admin's "Share URL" button copies.
 */
export default function PromoShare() {
  const { id } = useParams();
  const [promo, setPromo] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/promo/public/${id}`)
      .then(r => setPromo(r.data))
      .catch(e => setError(e.response?.data?.detail || "Promo not found"));
  }, [id]);

  const CAPTION = (
    "✨ New on ProSeCraft ✨\n\n" +
    "A dragon-blood mafia king meets a swan who refuses to bow.\n" +
    "Every reply is yours to write. Every chapter bends to you.\n\n" +
    "19 AI companions · Steamy mode · Free to start · prosecraft.org\n\n" +
    "#BookTok #DarkRomance #Beastkin #MafiaRomance #AIRomance #ProSeCraft #RomanceBooks #ReadersOfTikTok"
  );

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Film className="h-10 w-10 text-rose-400 mx-auto mb-4" />
        <h1 className="font-serif text-3xl font-bold mb-3">{error}</h1>
      </div>
    );
  }
  if (!promo) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading promo…
      </div>
    );
  }

  const videoUrl = fileUrl(promo.download_url);
  const isVertical = promo.height > promo.width;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.info("Copy failed — long-press to copy manually"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
      <div className="mb-6 text-center">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-400 mb-2">ProSeCraft · Promo</div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold">A dragon &amp; a swan.</h1>
        <p className="text-slate-400 text-sm mt-2">Download for TikTok, Reels, Shorts, or share this link anywhere.</p>
      </div>

      <div className={`mx-auto rounded-3xl overflow-hidden border border-white/10 bg-black mb-6 ${isVertical ? "max-w-sm aspect-[9/16]" : "max-w-3xl aspect-video"}`}>
        <video data-testid="promo-video" src={videoUrl} controls playsInline className="w-full h-full object-contain" />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
        <a
          data-testid="promo-share-download"
          href={videoUrl}
          download={`prosecraft-promo-${promo.video_id}.mp4`}
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11 px-6"
        >
          <Download className="h-4 w-4" /> Download MP4 ({(promo.size/1e6).toFixed(1)} MB)
        </a>
        <button
          data-testid="promo-share-copy-url"
          onClick={() => copy(window.location.href, "Share link")}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 text-slate-200 hover:border-amber-400/60 hover:text-amber-300 h-11 px-5"
        >
          <Share2 className="h-4 w-4" /> Copy share link
        </button>
        <button
          data-testid="promo-share-copy-caption"
          onClick={() => copy(CAPTION, "TikTok / Reels caption")}
          className="inline-flex items-center gap-2 rounded-full border border-rose-400/50 bg-rose-500/10 text-rose-200 hover:bg-rose-500/15 h-11 px-5"
        >
          <Copy className="h-4 w-4" /> Copy TikTok caption
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0F1117] p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Suggested caption</div>
        <pre data-testid="promo-caption-preview" className="whitespace-pre-wrap text-sm text-slate-200 font-sans leading-relaxed">{CAPTION}</pre>
      </div>

      <div className="mt-10 text-center text-xs text-slate-500 font-mono uppercase tracking-widest">
        {promo.width}×{promo.height} · {Math.round(promo.duration_s)}s · generated {new Date(promo.created_at).toLocaleDateString()}
      </div>
    </div>
  );
}
